import { useContext, useEffect, useState } from 'react';
import ComponenteNCM from '../../../componenteNCM/componenteNCM';
import style from './receitaBrutaCalculada.module.scss';
import { ETipoOperacao } from '../../../../../../../../../enums/ETipoOperacao';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { PaginasOutrosRegimes } from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import {
  INCMReceitaBrutaCalculada,
  IReceitaBrutaCalculadaNCMReceber,
} from '../../../../../../../../../interfaces/IReceitaBrutaCalculada';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  setBaseDeCalculo: React.Dispatch<React.SetStateAction<PaginasOutrosRegimes>>;
}

const ReceitaBrutaCalculada = ({ isEdicao, setIsEdicao, setBaseDeCalculo }: IProps) => {
  const [isEdicaoNcm, setIsEdicaoNcm] = useState<boolean>(false);
  const [isLoadingPost, setIsLoadingPost] = useState<boolean>(false);

  const [listaAgregada, setListaAgregada] = useState<INCMReceitaBrutaCalculada[]>([]);

  const context = useContext(DiefCfemContext);
  const idReceitaBrutaCalculada = context?.idReceitaBrutaCalculada ?? -1;

  const { postReceitaBrutaCalculada, deleteReceitaBrutaCalculada, getListaNCMsAdicionadosReceitaBrutaCalculada } =
    useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  const {
    data: listaNCMData,
    isLoading: isListaNCMLoading,
    error: listaNCMError,
    refetch: refetchListaNCM,
  } = useQuery<IReceitaBrutaCalculadaNCMReceber, IErrorWithSentryId>({
    queryKey: ['getListaNCMsAdicionadosReceitaBrutaCalculada', idReceitaBrutaCalculada],
    queryFn: () => getListaNCMsAdicionadosReceitaBrutaCalculada(idReceitaBrutaCalculada),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: idReceitaBrutaCalculada !== -1,
  });

  useEffect(() => {
    if (listaNCMData) {
      const lista: INCMReceitaBrutaCalculada[] = [];

      if (listaNCMData.ncmsPrecoCorrente) {
        lista.push(...listaNCMData.ncmsPrecoCorrente);
      }

      if (listaNCMData.ncmsValorReferencia) {
        lista.push(...listaNCMData.ncmsValorReferencia);
      }

      setListaAgregada(lista);
    }
  }, [listaNCMData]);

  useEffect(() => {
    if (!isEdicao) {
      setIsLoadingPost(true);

      postReceitaBrutaCalculada(context?.idOperacaoEditar || '-1')
        .then((response) => {
          context?.setIdReceitaBrutaCalculada(response.id);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao iniciar Receita Bruta Calculada, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
          setBaseDeCalculo('lista');
        })
        .finally(() => {
          setIsLoadingPost(false);
        });
    }
  }, []);

  const excluirReceitaBrutaCalculada = () => {
    deleteReceitaBrutaCalculada(context?.idReceitaBrutaCalculada || -1)
      .then(() => {
        setIsEdicao(false);
        setBaseDeCalculo('lista');
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao cancelar Receita Bruta Calculada, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  const onClickSalvarBaseCalculo = () => {
    if (listaNCMData?.ncmsPrecoCorrente?.length === 0 && listaNCMData?.ncmsValorReferencia?.length === 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Insira pelo menos um NCM para continuar.',
      });
    } else {
      setBaseDeCalculo('lista');
    }
  };

  return (
    <div>
      {isLoadingPost == true && (
        <div className={style.loadingContainer}>
          <BrLoading large />
        </div>
      )}

      {isLoadingPost == false && listaNCMError != null && (
        <ErrorMessage
          errorMessage={''}
          error={listaNCMError}
          onClickFunction={() => {
            setIsEdicao(false);
            setBaseDeCalculo('lista');
          }}
        />
      )}

      {isLoadingPost == false && listaNCMError == null && (
        <>
          <div className={style.titleContainer}>
            <span className={style.title}>Operação - Receita Bruta Calculada</span>
          </div>

          <div className={style.ncmContainer}>
            <ComponenteNCM
              isEdicaoNcm={isEdicaoNcm}
              setIsEdicaoNcm={setIsEdicaoNcm}
              ncmListaData={listaAgregada}
              isListaNCMLoading={isListaNCMLoading}
              listaNCMError={listaNCMError}
              refetchListaNCM={refetchListaNCM}
              type={ETipoOperacao.RECEITA_BRUTA_CALCULADA}
              insideTitle
            />
          </div>

          <div className={style.containerButton}>
            {!isEdicao && (
              <BrButton secondary onClick={() => excluirReceitaBrutaCalculada()} data-testid='btn-cancelar'>
                <span className={style.button}>
                  <i className={`fas fa-trash`} /> Cancelar
                </span>
              </BrButton>
            )}
            <BrButton onClick={() => onClickSalvarBaseCalculo()} primary data-testid='btn-salvar'>
              <span className={style.button}>
                {isEdicao && (
                  <>
                    <i className={`fas fa-save`} /> Salvar Base de Cálculo
                  </>
                )}

                {!isEdicao && (
                  <>
                    <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                  </>
                )}
              </span>
            </BrButton>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceitaBrutaCalculada;
